import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { ArrowRight, Lightbulb, Rocket, Settings, Zap, Users2, BarChart3, Bot, FileSpreadsheet, CheckCircle2, TrendingUp, Target, FileText, Globe, Brain } from "lucide-react";
import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import { ScoreGauge } from "@/components/ScoreGauge";
import { LevelBadge } from "@/components/LevelBadge";
import { LeadForm, LeadFormData } from "@/components/LeadForm";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { logger } from "@/lib/logger";
import { useWebsiteScan } from "@/hooks/use-website-scan";
import { DownloadReportEmailForm } from "@/components/DownloadReportEmailForm";
import { ReportFooter } from "@/components/ReportFooter";
import { QuizAILoader } from "@/components/QuizAILoader";
import { AIInsightCard } from "@/components/AIInsightCard";
import { Skeleton } from "@/components/ui/skeleton";
import { useReportTracking } from "@/hooks/use-report-tracking";

const ReportMid = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [checkedItems, setCheckedItems] = useState<Record<string, boolean>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [aiAnalysis, setAiAnalysis] = useState<any>(null);
  const [isLoadingAI, setIsLoadingAI] = useState(true);
  
  const { score, answers } = location.state || {};
  const band = 'mid';
  const reportToken = (location.state as any)?.reportToken;
  const quizSubmissionId = (location.state as any)?.quizSubmissionId;
  const leadSubmissionId = (location.state as any)?.leadSubmissionId;
  
  // Track report view
  useReportTracking({
    reportToken,
    quizSubmissionId,
    leadSubmissionId
  });
  
  const { logoUrl } = useWebsiteScan({
    initialScan: (location.state as any)?.websiteScan,
    domain: (answers?.[4] as string) || undefined,
    quizSubmissionId: quizSubmissionId || undefined,
  });
  
  // Helper function to format domain for display
  const formatDomainForDisplay = (domain: string): string => {
    if (!domain) return '';
    return domain
      .replace(/^https?:\/\//, '')
      .replace(/^www\./, '')
      .trim();
  };
  
  // Track report view
  useEffect(() => {
    if (typeof window !== 'undefined' && window.fbq) {
      // Custom event
      window.fbq('trackCustom', 'ReportViewed', {
        report_type: band,
        score: score,
        timestamp: new Date().toISOString()
      });
      
      // Standard ViewContent event
      window.fbq('track', 'ViewContent', {
        content_name: 'LinkedIn Readiness Report - Mid',
        content_category: 'report',
        content_type: band,
        value: score
      });
    }
  }, [band, score]);
  
  // Load AI analysis from database (already generated during quiz completion)
  useEffect(() => {
    const loadAnalysis = async () => {
      try {
        setIsLoadingAI(true);
        const quizSubmissionId = (location.state as any)?.quizSubmissionId;
        
        if (quizSubmissionId) {
          logger.debug('Loading AI analysis from database for quiz:', quizSubmissionId);
          
          // Try to fetch from database first
          const { data: quizData, error: dbError } = await supabase
            .from('quiz_submissions')
            .select('ai_analysis')
            .eq('id', quizSubmissionId)
            .single();
          
          if (dbError) {
            console.error('Error loading AI analysis from database:', dbError);
          } else if (quizData?.ai_analysis) {
            logger.debug('AI analysis loaded from database');
            setAiAnalysis(quizData.ai_analysis);
            setIsLoadingAI(false);
            return;
          }
        }
        
        // Fallback: Generate if not in database (legacy quizzes)
        logger.debug('No AI analysis in database, generating as fallback');
        const quizData = {
          answers: {
            ...(answers || {}),
            found_linkedin_profile: (location.state as any)?.foundLinkedInProfile
          },
          websiteScan: (location.state as any)?.websiteScan || {},
          competitorAnalysis: (location.state as any)?.competitorAnalysis || {},
          interstitialAnswers: (location.state as any)?.interstitialAnswers || {}
        };

        const { data, error } = await supabase.functions.invoke('generate-quiz-recommendations', {
          body: { quizData, band: 'mid', quizSubmissionId }
        });

        if (error) {
          console.error('Error generating AI analysis:', error);
          setAiAnalysis(null);
        } else if (data?.analysis) {
          setAiAnalysis(data.analysis);
          console.log('✅ AI analysis generated and saved by edge function');
        }
      } catch (error) {
        console.error('Failed to load AI analysis:', error);
        setAiAnalysis(null);
      } finally {
        setIsLoadingAI(false);
      }
    };

    if (answers && Object.keys(answers).length > 0) {
      loadAnalysis();
    } else {
      setIsLoadingAI(false);
    }
  }, [answers, band, location.state]);
  
  const handleSubmit = async (data: LeadFormData) => {
    setIsSubmitting(true);
    
    try {
      logger.debug('Submitting lead via edge function');
      
      const quizSubmissionId = (location.state as any)?.quizSubmissionId || null;
      
      const { data: result, error } = await supabase.functions.invoke('submit-lead', {
        body: {
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
          phone: data.phone,
          company: null,
          position: null,
          quizScore: score,
          quizBand: band,
          quizAnswers: answers,
          quizSubmissionId: quizSubmissionId
        }
      });

      if (error) {
        logger.error('Submission error', error);
        throw error;
      }

      if (!result.success) {
        throw new Error(result.error || 'Submission failed');
      }

      logger.info('Lead submission successful');
      
      // Track headline conversion with lead_submission_id
      const sessionId = localStorage.getItem('headline_session_id');
      const variantId = localStorage.getItem('assigned_variant_id');
      
      if (sessionId && variantId && result.leadId) {
        try {
          await supabase.from('headline_conversions').insert({
            variant_id: variantId,
            session_id: sessionId,
            lead_submission_id: result.leadId,
            quiz_submission_id: quizSubmissionId || null
          });
          logger.info('✅ Lead conversion tracked for A/B test');
        } catch (convError) {
          logger.error('❌ Failed to track conversion', convError);
        }
      }
      
      // Track lead form submission
      if (typeof window !== 'undefined' && window.fbq) {
        window.fbq('trackCustom', 'LeadFormSubmitted', {
          report_band: band,
          score: score,
          form_type: 'Report Lead Form'
        });
        
        // Also track as standard Lead event
        window.fbq('track', 'Lead', {
          content_name: 'LinkedIn Readiness Report',
          content_category: band,
          value: score
        });
      }
      
      // Push lead form submission to GTM dataLayer
      if (typeof window !== 'undefined' && (window as any).dataLayer) {
        (window as any).dataLayer.push({
          event: 'lead_form_submitted',
          lead_id: result.leadId,
          report_band: band,
          quiz_score: score,
          email: data.email,
          event_category: 'lead_generation',
          event_label: `Band: ${band} | Score: ${score}`
        });
        console.log('✅ GTM lead form submission event pushed');
      }
      
      toast.success("Dziękujemy! Twój raport został zapisany.");
      setIsSubmitting(false);
      setIsDialogOpen(false);
      
      setTimeout(() => {
        navigate("/dziekujemy");
      }, 500);
    } catch (error) {
      logger.error('Error saving lead', error);
      toast.error("Wystąpił błąd. Spróbuj ponownie.");
      setIsSubmitting(false);
    }
  };
  
  const quickWins = [
    { id: "1", label: "Content calendar na 3 miesiące (3x/tyg.)" },
    { id: "2", label: "Biblioteka 15+ content assets (PDF, case studies)" },
    { id: "3", label: "Automatyzacja leadów + integracja CRM" },
    { id: "4", label: "Lead scoring i segmentacja" },
    { id: "5", label: "Employee advocacy program (5+ osób)" },
    { id: "6", label: "Dashboard ROI i KPI LinkedIn" }
  ];
  
  const recommendations = [
    {
      icon: <Rocket className="h-10 w-10 text-primary" />,
      title: "Skaluj publikacje: 3-5 postów tygodniowo",
      description: "Zespół sprzedaży i marketingu powinien systematycznie budować widoczność przez wartościowe, różnorodne treści.",
      howTo: [
        "Stwórz content calendar na 3 miesiące do przodu",
        "Mix formatów: case studies, posty eksperckie, Q&A, video",
        "Zaangażuj zespół - każdy ekspert publikuje raz w miesiącu",
        "A/B testuj najlepsze godziny publikacji dla Twojej grupy docelowej",
        "Recycling treści: zmień format (post → carousel → video)"
      ]
    },
    {
      icon: <FileSpreadsheet className="h-10 w-10 text-primary" />,
      title: "Zbuduj bibliotekę content assets",
      description: "Przygotuj 15-20 zasobów: PDF-y, ebooki, templates, case studies. To Twój arsenał do budowania zaufania i lead generation.",
      howTo: [
        "Stwórz 3-5 'lead magnets' (checklisty, raporty, templates)",
        "Udokumentuj 5+ case studies z konkretnymi wynikami",
        "Przygotuj FAQ + objection handling w formie materiałów",
        "Każdy asset powinien mieć jasne CTA i landing page",
        "Trackuj pobrania i ROI każdego materiału"
      ]
    },
    {
      icon: <Bot className="h-10 w-10 text-primary" />,
      title: "Wdróż automatyzację i integrację z CRM",
      description: "Zaawansowane narzędzia do automatyzacji prospectingu, nurturingu i trackingu leadów. Oszczędzaj czas i zwiększ skuteczność.",
      howTo: [
        "Wybierz narzędzie: LinkedIn Sales Navigator + automation tool",
        "Integracja z CRM (HubSpot/Salesforce/Pipedrive)",
        "Automatyczne dodawanie leadów do sekwencji nurturing",
        "Lead scoring: gorące/ciepłe/zimne według zaangażowania",
        "Weekly reports: nowe leady, engagement rate, conversion"
      ]
    },
    {
      icon: <Users2 className="h-10 w-10 text-primary" />,
      title: "Employee Advocacy - aktywizuj zespół",
      description: "Zaangażuj 5-10 ekspertów z firmy do regularnego budowania personal brandingu. Zasięg rośnie wykładniczo.",
      howTo: [
        "Wybierz 5-10 kluczowych osób (sales, marketing, eksperci)",
        "Szkolenie z personal brandingu i pisania na LinkedIn",
        "Wspólny bank pomysłów na treści",
        "Każda osoba publikuje 1-2x w miesiącu minimum",
        "Wzajemny engagement w zespole (komentarze, share)"
      ]
    },
    {
      icon: <BarChart3 className="h-10 w-10 text-primary" />,
      title: "Mierz ROI i optymalizuj działania",
      description: "Dashboard z kluczowymi metrykami LinkedIn: leady, pipeline, konwersje. Data-driven optimization.",
      howTo: [
        "Trackuj źródło każdego leadu w CRM (tag: LinkedIn)",
        "KPI: impressions, engagement rate, lead volume, conversion",
        "Koszt pozyskania leadu (CAC) vs wartość (LTV)",
        "Miesięczny report dla zarządu z ROI",
        "Optymalizuj: więcej tego co działa, mniej co nie działa"
      ]
    },
    {
      icon: <Target className="h-10 w-10 text-primary" />,
      title: "Prospecting & Enrichment - znajdź telefon i email decydentów",
      description: "Wzbogać swoją bazę leadów o dane kontaktowe (email służbowy, telefon). Dzięki temu możesz działać wielokanałowo i zwiększyć skuteczność o 40-60%.",
      howTo: [
        "Wybierz narzędzie do enrichmentu (Apollo.io, Hunter.io, RocketReach, Lusha)",
        "Import listy leadów z LinkedIn Sales Navigator lub CRM",
        "Automatyczne wzbogacenie: email, telefon, rola, seniority",
        "Weryfikacja jakości danych (bounce rate <5%)",
        "Compliance z RODO: opt-out link, źródło danych, zgody",
        "Integracja wzbogaconych danych z CRM i automation tools"
      ]
    },
    {
      icon: <Zap className="h-10 w-10 text-primary" />,
      title: "Lead Gen Automation - automatyzuj sekwencje i oszczędzaj czas",
      description: "Narzędzia automation pozwalają na wysyłanie spersonalizowanych wiadomości na skalę, auto-follow-up i smart nurturing. To oszczędza 10-15h tygodniowo.",
      howTo: [
        "Wybierz narzędzie automation (Waalaxy, Expandi, Lemlist, Dux-Soup)",
        "Zbuduj sekwencję multi-touch: connection → wiadomość → follow-up → email",
        "Personalizacja na skalę: używaj zmiennych (imię, firma, rola)",
        "Ustawienia bezpieczeństwa: limity dzienne (50-80 invites/day max)",
        "A/B testing różnych wariantów wiadomości",
        "Monitoring i optymalizacja: acceptance rate, reply rate, conversion"
      ]
    }
  ];

  const stats = [
    { label: "Czas wdrożenia", value: "kilka tygodni", progress: 60 },
    { label: "Wymagany budżet", value: "średni", progress: 50 },
    { label: "Potencjał wzrostu", value: "wysoki", progress: 85 }
  ];

  const advancedTips = [
    {
      icon: <Zap className="h-6 w-6 text-primary" />,
      title: "LinkedIn Ads jako booster",
      description: "Promuj najlepsze posty i lead magnety reklamami. Budget 500-1000 PLN/mies daje zauważalne efekty."
    },
    {
      icon: <Target className="h-6 w-6 text-primary" />,
      title: "Account Based Marketing (ABM)",
      description: "Dla top 20-50 kont: spersonalizowane treści, targetowane reklamy, koordynowane działania zespołu."
    },
    {
      icon: <TrendingUp className="h-6 w-6 text-primary" />,
      title: "LinkedIn Events i Live'y",
      description: "Organizuj webinary i live Q&A na LinkedIn. Średnio 30-40% uczestników to ciepłe leady."
    }
  ];
  
  return (
    <>
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <ScrollArea className="h-screen w-full">
      <div className="min-h-screen gradient-subtle py-6 md:py-12">
        <div className="container px-3 sm:px-4">
          <div className="max-w-5xl mx-auto space-y-6 md:space-y-8 pb-12">
            {/* Domain header - always visible */}
            <Card className="gradient-hero border-none shadow-xl mb-6">
              <CardContent className="p-6 md:p-8 text-center">
                <div className="flex flex-col items-center gap-4">
                  {logoUrl ? (
                    <div className="rounded-lg bg-white p-3 shadow-lg">
                      <img 
                        src={logoUrl} 
                        alt="Company logo" 
                        className="h-12 w-auto max-w-[200px] object-contain"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                          e.currentTarget.nextElementSibling?.classList.remove('hidden');
                        }}
                      />
                      <div className="hidden rounded-full bg-white/20 p-3">
                        <Globe className="h-8 w-8 text-white" />
                      </div>
                    </div>
                  ) : (
                    <div className="rounded-full bg-white/20 p-3">
                      <Globe className="h-8 w-8 text-white" />
                    </div>
                  )}
                  <div>
                    {answers?.[4] && (answers[4] as string).trim() !== '' ? (
                      <>
                        <p className="text-white/90 text-lg mb-2">
                          Osiągnięty wynik dla
                        </p>
                        <h2 className="text-3xl md:text-4xl font-bold text-white">
                          {formatDomainForDisplay(answers[4] as string)}
                        </h2>
                      </>
                    ) : (
                      <h2 className="text-3xl md:text-4xl font-bold text-white">
                        Twój osiągnięty wynik
                      </h2>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Score Section - only show if score exists */}
            {score && (
              <Card className="gradient-card border-none shadow-2xl overflow-hidden">
                <CardContent className="p-0">
                  {/* Top section with gradient background */}
                  <div className="bg-gradient-to-br from-primary/5 via-primary/10 to-primary/5 px-6 pt-12 pb-8">
                    <div className="text-center mb-8">
                      <h2 className="text-4xl md:text-5xl font-bold mb-3 bg-gradient-to-r from-primary via-primary/90 to-primary bg-clip-text text-transparent">
                        Średniozaawansowany
                      </h2>
                      <p className="text-lg text-muted-foreground">
                        Poziom gotowości LinkedIn
                      </p>
                    </div>
                    
                    {/* Score and Badge in elegant layout */}
                    <div className="flex flex-col md:flex-row items-center justify-center gap-8 md:gap-16">
                      <div className="transform hover:scale-105 transition-transform duration-300">
                        <ScoreGauge score={score} />
                      </div>
                      <div className="transform hover:scale-105 transition-transform duration-300">
                        <LevelBadge level="mid" />
                      </div>
                    </div>
                  </div>
                  
                  {/* Bottom section with AI-generated description */}
                  <div className="px-6 py-8 bg-card">
                    <div className="max-w-3xl mx-auto">
                      {isLoadingAI ? (
                        <QuizAILoader />
                      ) : aiAnalysis ? (
                        aiAnalysis.areas ? (
                          <div className="space-y-4">
                            {aiAnalysis.areas.map((area: any, idx: number) => (
                              <AIInsightCard
                                key={idx}
                                title={area.name}
                                content={area.content}
                                icon={<TrendingUp className="h-5 w-5 text-primary" />}
                                variant="highlight"
                              />
                            ))}
                            {aiAnalysis.keyInsights && aiAnalysis.keyInsights.length > 0 && (
                              <AIInsightCard
                                title="Kluczowe obserwacje"
                                content={aiAnalysis.keyInsights}
                                icon={<Brain className="h-5 w-5 text-primary" />}
                                variant="default"
                              />
                            )}
                          </div>
                        ) : (
                          <AIInsightCard
                            title={aiAnalysis.title || "Co oznacza Twój wynik"}
                            content={aiAnalysis.description}
                            icon={<TrendingUp className="h-5 w-5 text-primary" />}
                            variant="highlight"
                          />
                        )
                      ) : (
                        <AIInsightCard
                          title="Co oznacza Twój wynik"
                          content={[
                            "Masz już podstawy LinkedIn Social Selling na miejscu, co stawia Cię w lepszej pozycji niż większość firm w Polsce. Teraz kluczowe jest przejście z działań ad-hoc do systematycznej strategii content marketingu i employee advocacy.",
                            "Z odpowiednim podejściem do contentu i zaangażowaniem zespołu, możecie zwiększyć swój zasięg o 300-500% w ciągu 6 miesięcy i stać się rozpoznawalnym thought leaderem w swojej branży."
                          ]}
                          icon={<Brain className="h-5 w-5 text-primary" />}
                          variant="default"
                        />
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
            
            
            {/* CTA Button to open form modal */}
            <Card className="gradient-cta-red border-none shadow-xl overflow-hidden">
              <CardHeader className="text-center space-y-6 pb-8">
                <div className="flex justify-center">
                  <div className="inline-flex items-center gap-2 bg-white/20 text-white px-4 py-2 rounded-full text-sm font-medium backdrop-blur-sm">
                    <Zap className="h-4 w-4" />
                    Zacznij działać i zbuduj nowy skuteczny kanał sprzedaży
                  </div>
                </div>
                
                <CardTitle className="text-2xl md:text-4xl text-white leading-tight px-4">
                  Dowiedz się, jak LinkedIn buduje kontakty, sprzedaż i reputację lidera.
                </CardTitle>
                
                <div className="max-w-2xl mx-auto space-y-4 px-4">
                  <h3 className="text-xl md:text-2xl font-semibold text-white/95">
                    Masz już podstawy. Teraz czas przyspieszyć LinkedIn.
                  </h3>
                  
                  <p className="text-base text-white/90 leading-relaxed">
                    Twój wynik pokazuje, że LinkedIn już działa, ale nie wykorzystuje pełnego potencjału. Pokażemy Ci, jak z obecnych działań zbudować przewidywalny kanał sprzedaży, który regularnie dowozi nowe rozmowy handlowe z idealnymi klientami.
                  </p>
                </div>

                {/* Benefits list */}
                <div className="max-w-2xl mx-auto pt-4 space-y-3 text-left px-4">
                  <div className="flex items-start gap-3 bg-white/10 backdrop-blur-sm rounded-lg p-4 hover:bg-white/15 transition-colors">
                    <CheckCircle2 className="h-6 w-6 text-white shrink-0 mt-0.5" />
                    <p className="text-white/95 font-medium">Poznasz sekrety LinkedIn oraz strategie, które wykorzystują liderzy w B2B</p>
                  </div>
                  
                  <div className="flex items-start gap-3 bg-white/10 backdrop-blur-sm rounded-lg p-4 hover:bg-white/15 transition-colors">
                    <CheckCircle2 className="h-6 w-6 text-white shrink-0 mt-0.5" />
                    <p className="text-white/95 font-medium">Kierunek skalowania działań na najbliższe miesiące</p>
                  </div>
                  
                  <div className="flex items-start gap-3 bg-white/10 backdrop-blur-sm rounded-lg p-4 hover:bg-white/15 transition-colors">
                    <CheckCircle2 className="h-6 w-6 text-white shrink-0 mt-0.5" />
                    <p className="text-white/95 font-medium">Orientacyjny koszt wdrożenia procesu Social Selling w Twojej firmie</p>
                  </div>
                </div>
              </CardHeader>
              
              <CardContent className="pt-0 pb-8 px-4 space-y-6">
                <div className="max-w-2xl mx-auto space-y-4">
                  <Button 
                    size="lg" 
                    variant="secondary"
                    className="w-full flex items-center justify-center gap-2 text-lg py-6 h-auto bg-white text-[hsl(var(--cta-red))] hover:bg-white/90 hover:scale-105 transition-all shadow-xl"
                    onClick={() => {
                      if (typeof window !== 'undefined' && window.fbq) {
                        window.fbq('trackCustom', 'CTAClicked', {
                          cta_type: 'Scale LinkedIn 90 Days',
                          report_band: band,
                          button_text: 'Zobacz jak skalować LinkedIn w 90 dni'
                        });
                      }
                      document.getElementById('lead-form-trigger-mid')?.click();
                    }}
                  >
                    Zobacz jak skalować LinkedIn w 90 dni
                    <ArrowRight className="h-5 w-5" />
                  </Button>
                  
                  <p className="text-sm text-white/80 text-center max-w-xl mx-auto leading-relaxed">
                    Rozmowa wsparta Twoim wynikiem i 8+ latami doświadczenia w LinkedIn. Konkretne rekomendacje dla Twojej sytuacji.
                  </p>
                </div>

                {/* Social proof section */}
                <div className="max-w-2xl mx-auto pt-6 border-t border-white/20">
                  <div className="flex items-center justify-center gap-3 text-white/90">
                    <div className="flex -space-x-2">
                      <div className="w-8 h-8 rounded-full bg-white/20 border-2 border-white flex items-center justify-center">
                        <TrendingUp className="h-4 w-4 text-white" />
                      </div>
                      <div className="w-8 h-8 rounded-full bg-white/20 border-2 border-white flex items-center justify-center">
                        <BarChart3 className="h-4 w-4 text-white" />
                      </div>
                      <div className="w-8 h-8 rounded-full bg-white/20 border-2 border-white flex items-center justify-center">
                        <Target className="h-4 w-4 text-white" />
                      </div>
                    </div>
                    <p className="text-sm font-medium">
                      Firmy na podobnym poziomie widzą wyraźny wzrost jakościowych rozmów handlowych
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Header */}
            <div className="text-center mb-12 md:mb-16 mt-28 md:mt-40 animate-fade-in">
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 md:mb-6 bg-gradient-to-r from-primary via-primary/80 to-primary/60 bg-clip-text text-transparent px-2">
                Jak przyspieszyć pipeline przez LinkedIn
              </h1>
              <p className="text-base sm:text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto px-4">
                Masz solidne podstawy. Teraz czas skalować procesy, systematyzować publikacje, działania lead generation i mierzyć ROI.
              </p>
            </div>

            {/* Stats Overview */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-6 mb-6 md:mb-8">
              {stats.map((stat, index) => (
                <Card key={index} className="border-none shadow-lg">
                  <CardContent className="p-4 md:p-6">
                    <div className="text-xs md:text-sm text-muted-foreground mb-1 md:mb-2">{stat.label}</div>
                    <div className="text-xl md:text-2xl font-bold mb-2 md:mb-3">{stat.value}</div>
                    <Progress value={stat.progress} className="h-2" />
                  </CardContent>
                </Card>
              ))}
            </div>
            
            {/* Main Recommendations */}
            <Card className="gradient-card border-none shadow-xl">
              <CardHeader className="p-4 md:p-6">
                <CardTitle className="text-xl sm:text-2xl md:text-3xl flex items-center gap-2 md:gap-3">
                  <Rocket className="h-6 w-6 md:h-8 md:w-8 text-primary flex-shrink-0" />
                  <span>Strategia skalowania i systematyzacji</span>
                </CardTitle>
                <CardDescription className="text-sm md:text-base">
                  Działania które przeniosą Cię na wyższy poziom i przyspieszą generowanie pipeline
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4 md:space-y-8 p-4 md:p-6">
                {recommendations.map((rec, index) => (
                  <div key={index} className="group">
                    <div className="flex flex-col sm:flex-row gap-4 md:gap-6 p-4 md:p-6 rounded-xl bg-gradient-to-br from-secondary/50 to-secondary/30 hover:shadow-lg transition-all">
                      <div className="flex-shrink-0">{rec.icon}</div>
                      <div className="flex-1">
                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-3">
                          <h3 className="font-bold text-lg md:text-xl">{rec.title}</h3>
                          <span className="text-xs md:text-sm font-semibold text-primary bg-primary/10 px-2 md:px-3 py-1 rounded-full whitespace-nowrap self-start">
                            Priorytet {index + 1}
                          </span>
                        </div>
                        <p className="text-muted-foreground mb-3 md:mb-4 text-sm md:text-base">{rec.description}</p>
                        <div className="bg-background/50 rounded-lg p-3 md:p-4 space-y-2">
                          <div className="font-semibold text-xs md:text-sm mb-2 md:mb-3 flex items-center gap-2">
                            <CheckCircle2 className="h-3 w-3 md:h-4 md:w-4 text-primary flex-shrink-0" />
                            Plan działania:
                          </div>
                          <ul className="space-y-1.5 md:space-y-2">
                            {rec.howTo.map((step, i) => (
                              <li key={i} className="flex gap-2 md:gap-3 text-xs md:text-sm">
                                <span className="text-primary font-bold flex-shrink-0">{i + 1}.</span>
                                <span className="text-muted-foreground">{step}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Advanced Tips */}
            <Card className="border-none shadow-xl bg-gradient-to-br from-primary/5 to-background">
              <CardHeader className="p-4 md:p-6">
                <CardTitle className="text-xl md:text-2xl flex items-center gap-2 md:gap-3">
                  <Lightbulb className="h-5 w-5 md:h-7 md:w-7 text-primary flex-shrink-0" />
                  <span>Zaawansowane taktyki dla średniozaawansowanych</span>
                </CardTitle>
                <CardDescription className="text-sm md:text-base">
                  Dodatkowe działania które mogą znacząco zwiększyć efektywność
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3 md:space-y-4 p-4 md:p-6">
                {advancedTips.map((tip, index) => (
                  <div key={index} className="flex gap-3 md:gap-4 p-4 md:p-5 rounded-lg bg-background border border-primary/20 hover:border-primary/40 transition-colors">
                    <div className="flex-shrink-0">{tip.icon}</div>
                    <div className="flex-1">
                      <div className="font-bold mb-1 md:mb-2 text-sm md:text-base">{tip.title}</div>
                      <div className="text-xs md:text-sm text-muted-foreground">{tip.description}</div>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
            
            {/* Quick Wins */}
            <Card className="gradient-card border-none shadow-xl">
              <CardHeader className="p-4 md:p-6">
                <CardTitle className="text-xl md:text-2xl flex items-center gap-2 md:gap-3">
                  <Settings className="h-5 w-5 md:h-7 md:w-7 text-primary flex-shrink-0" />
                  <span>Quick Wins - Checklist wdrożeniowa</span>
                </CardTitle>
                <CardDescription className="text-sm md:text-base">
                  Priorytetowe działania na najbliższe 60-90 dni
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-2 md:space-y-3 p-4 md:p-6">
                {quickWins.map((item) => (
                  <div key={item.id} className="flex items-center space-x-3 md:space-x-4 p-3 md:p-4 rounded-lg hover:bg-secondary/50 transition-all border border-secondary">
                    <Checkbox
                      id={item.id}
                      checked={checkedItems[item.id] || false}
                      onCheckedChange={(checked) =>
                        setCheckedItems(prev => ({ ...prev, [item.id]: checked === true }))
                      }
                      className="h-4 w-4 md:h-5 md:w-5 flex-shrink-0"
                    />
                    <label
                      htmlFor={item.id}
                      className="text-sm md:text-base font-medium leading-snug cursor-pointer flex-1"
                    >
                      {item.label}
                    </label>
                  </div>
                ))}
              </CardContent>
            </Card>
            
            {/* Download Report CTA */}
            <Card className="gradient-cta-red border-none shadow-2xl text-white overflow-hidden relative">
              <div className="absolute inset-0 bg-gradient-to-r from-white/5 to-transparent" />
              <CardContent className="p-8 md:p-12 relative z-10">
                <div className="max-w-3xl mx-auto text-center space-y-8">
                  <div>
                    <h2 className="text-3xl md:text-4xl font-bold mb-4">
                      Pobierz swój indywidualny raport i zalecenia
                    </h2>
                    <p className="text-lg opacity-90">
                      Zostaw swój email, a prześlemy Ci szczegółowy raport PDF z Twoimi wynikami i rekomendacjami.
                    </p>
                  </div>

                  <DownloadReportEmailForm 
                    quizSubmissionId={(location.state as any)?.quizSubmissionId} 
                    score={score}
                    band={band}
                  />
                </div>
              </CardContent>
            </Card>
            
          </div>
        </div>
      </div>
      <ReportFooter />
      
      {/* Hidden Dialog Trigger for Lead Form */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogTrigger asChild>
          <button id="lead-form-trigger-mid" className="hidden" />
        </DialogTrigger>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">Zobacz jak skalować LinkedIn w 90 dni</DialogTitle>
          </DialogHeader>
          <LeadForm
            onSubmit={handleSubmit}
            isSubmitting={isSubmitting}
          />
        </DialogContent>
      </Dialog>
    </ScrollArea>
    </>
  );
};

export default ReportMid;
